import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  flushOutbox,
  getGroupStatus,
  outboxHoldsReleased,
  selectCurrentGroupId,
  selectHeldCount,
  selectOutbox,
} from "../redux/reducer/app-slice";
import { useToast } from "./use-toast";

export const useOutboxSync = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { showError } = useToast();
  const outboxSize = useSelector((state) => selectOutbox(state).length);
  const heldCount = useSelector(selectHeldCount);
  const retryTimer = useRef(null);

  const flush = useCallback(
    (ignoreBackoff = false) =>
      dispatch(flushOutbox({ ignoreBackoff, onRejected: showError })).then(
        ({ retryAt } = {}) => {
          clearTimeout(retryTimer.current);
          if (retryAt) {
            retryTimer.current = setTimeout(
              () => flush(),
              Math.max(0, retryAt - Date.now()),
            );
          }
        },
      ),
    [dispatch, showError],
  );

  useEffect(() => {
    const onOnline = () => flush(true);
    const sendHeld = () => {
      dispatch(outboxHoldsReleased());
      flush(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        sendHeld();
        return;
      }
      flush(true).then(() =>
        dispatch(getGroupStatus(selectCurrentGroupId(store.getState()), true)),
      );
    };
    dispatch(outboxHoldsReleased());
    flush(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("pagehide", sendHeld);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pagehide", sendHeld);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearTimeout(retryTimer.current);
    };
  }, [dispatch, flush, store]);

  useEffect(() => {
    if (outboxSize > 0) flush();
  }, [outboxSize, heldCount, flush]);
};
